import type { StudentAssignment, StudentLesson } from "../types";

function escapeIcsText(s: string): string {
  return s
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r\n|\n|\r/g, "\\n");
}

function formatIcsDate(iso: string): string {
  return iso.replace(/-/g, "");
}

function dtendDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(y, m - 1, d + 1); // lokaler Konstruktor, kein UTC-Shift
  return [
    String(date.getFullYear()),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("");
}

function dtstamp(): string {
  return new Date().toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
}

function foldLine(line: string): string {
  const encoder = new TextEncoder();
  const bytes = encoder.encode(line);
  if (bytes.length <= 75) return line;

  const result: string[] = [];
  let byteOffset = 0;
  let first = true;

  while (byteOffset < bytes.length) {
    const maxBytes = first ? 75 : 74; // Fortsetzungszeilen: 1 Byte für Leerzeichen
    first = false;

    // Sicherstellen, dass wir nicht mitten in einem Multi-Byte-UTF-8-Zeichen schneiden
    let end = byteOffset + maxBytes;
    if (end >= bytes.length) {
      end = bytes.length;
    } else {
      // Zurückgehen bis zu einer sicheren Byte-Grenze (kein Continuation Byte 0x80–0xBF)
      while (end > byteOffset && (bytes[end] & 0xc0) === 0x80) end--;
    }

    const chunk = new TextDecoder().decode(bytes.slice(byteOffset, end));
    result.push(chunk);
    byteOffset = end;
  }

  return result.join("\r\n ");
}

export function generateIcs(assignments: StudentAssignment[]): string {
  const stamp = dtstamp();

  const events = assignments.map((a) => {
    const summary = a.practiceName
      ? `${a.departmentName} – ${a.practiceName}`
      : a.departmentName;

    const descParts: string[] = [`Fachbereich: ${a.departmentName}`];
    descParts.push(`Stunden/Woche: ${a.hoursPerWeek}`);
    if (a.practiceContact) descParts.push(`Ansprechpartner: ${a.practiceContact}`);
    if (a.practicePhone) descParts.push(`Telefon: ${a.practicePhone}`);
    if (a.practiceContactEmail) descParts.push(`E-Mail: ${a.practiceContactEmail}`);
    if (a.notes) descParts.push(`Notizen: ${a.notes}`);
    const description = descParts.map(escapeIcsText).join("\\n");

    const lines: string[] = [
      "BEGIN:VEVENT",
      // Die UID-Domain bleibt "mediskill", auch nach der Umbenennung: Kalender erkennen
      // Termine an der UID wieder, eine neue Domain ergäbe beim Import Dubletten.
      `UID:assignment-${a.id}@mediskill`,
      `DTSTAMP:${stamp}`,
      `DTSTART;VALUE=DATE:${formatIcsDate(a.startDate)}`,
      `DTEND;VALUE=DATE:${dtendDate(a.endDate)}`,
      `SUMMARY:${escapeIcsText(summary)}`,
    ];

    if (a.practiceAddress) {
      lines.push(`LOCATION:${escapeIcsText(a.practiceAddress)}`);
    }
    lines.push(`DESCRIPTION:${description}`);
    lines.push("END:VEVENT");

    return lines.map(foldLine).join("\r\n");
  });

  const calendar = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Meduvio//Schuelerportal//DE",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "X-WR-CALNAME:Meine Praktikumseinsätze",
    ...events,
    "END:VCALENDAR",
  ].join("\r\n");

  return calendar + "\r\n";
}

/** "2026-09-07" + "08:15" → "20260907T081500" */
function formatIcsDateTime(iso: string, time: string): string {
  const [h, m] = time.split(":");
  return `${iso.replace(/-/g, "")}T${h.padStart(2, "0")}${(m || "00").padStart(2, "0")}00`;
}

/**
 * Stundenplan als Kalenderdatei.
 *
 * Anders als bei den Praktikumseinsaetzen sind das Termine mit Uhrzeit. Sie gehen
 * bewusst als schwebende Zeit raus, also ohne "Z" und ohne TZID: der Unterricht
 * beginnt um 8:15 Ortszeit, egal in welcher Zeitzone der Kalender gerade steht.
 * Ein Umrechnen nach UTC wuerde den Plan bei Sommerzeitwechseln verschieben.
 */
export function generateLessonIcs(lessons: StudentLesson[]): string {
  const stamp = dtstamp();

  const events = lessons.map((l) => {
    const cancelled = l.status === "entfall";
    // Der Titel sagt es zusätzlich zu STATUS:CANCELLED, weil längst nicht jeder Kalender
    // abgesagte Termine sichtbar anders darstellt.
    const marks = [
      cancelled ? "entfällt" : l.status === "vertretung" ? "Vertretung" : "",
      l.isPreliminary ? "vorläufig" : "",
    ].filter(Boolean);
    const summary = marks.length > 0 ? `${l.subjectName} (${marks.join(", ")})` : l.subjectName;

    const descParts: string[] = [];
    if (l.lecturerName && !cancelled) descParts.push(`Dozent: ${l.lecturerName}`);
    if (cancelled) descParts.push("Diese Unterrichtsstunde fällt aus.");
    if (l.status === "vertretung") descParts.push("Diese Unterrichtsstunde findet mit einer Vertretung statt.");
    if (l.isPreliminary) descParts.push("Dieser Termin ist noch vorläufig.");

    const lines: string[] = [
      "BEGIN:VEVENT",
      `UID:lesson-${l.id}@mediskill`,
      `DTSTAMP:${stamp}`,
      `DTSTART:${formatIcsDateTime(l.date, l.startTime)}`,
      `DTEND:${formatIcsDateTime(l.date, l.endTime)}`,
      `SUMMARY:${escapeIcsText(summary)}`,
    ];
    // STATUS:CANCELLED ist der vorgesehene Weg nach RFC 5545. Der Termin bleibt damit im
    // Kalender stehen, statt beim naechsten Abgleich klanglos zu verschwinden.
    if (cancelled) lines.push("STATUS:CANCELLED");
    if (l.room) lines.push(`LOCATION:${escapeIcsText(l.room)}`);
    if (descParts.length > 0) {
      lines.push(`DESCRIPTION:${descParts.map(escapeIcsText).join("\\n")}`);
    }
    lines.push("END:VEVENT");

    return lines.map(foldLine).join("\r\n");
  });

  const calendar = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Meduvio//Schuelerportal//DE",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "X-WR-CALNAME:Mein Stundenplan",
    ...events,
    "END:VCALENDAR",
  ].join("\r\n");

  return calendar + "\r\n";
}
